import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Switch,
    FormControlLabel,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Paper,
    Snackbar,
    MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsIcon from '@mui/icons-material/Settings';
import { ComposedChart, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { useApi } from '../hooks/useApi';
import { getBaseURL, getApiHeaders, validateApiConfig } from '../config/api';

/**
 * Waterstof Prijs Pagina
 * 
 * Deze pagina toont waterstof prijzen voor verschillende types (grijs, groen, blauw)
 * en biedt instellingen voor prijs berekeningen.
 * 
 * Functies:
 * - Tab 1: Prijzengrafiek met switch tussen markt en retail
 * - Tab 2: Instellingen voor prijsberekening
 * - Real-time TTF prijzen
 * - Staafdiagram voor prijsopbouw (retail)
 */
const HydrogenPricePage = () => {
    // Valideer configuratie bij startup
    validateApiConfig();
    
    const { t } = useTranslation();
    const { post } = useApi();
    
    // State management
    const [tabValue, setTabValue] = useState(0);
    const [showRetail, setShowRetail] = useState(true);
    const [showBreakdown, setShowBreakdown] = useState(true);
    const [priceData, setPriceData] = useState(null);
    const [hiddenLines, setHiddenLines] = useState({});
    const [settings, setSettings] = useState({
        forecast_hours: 20,
        a: 0.05,
        b: 1.0,
        compressie: 1.5,
        opslag: 1.0,
        transport: 1.5,
        infra: 1.2,
        marge: 2.0,
        subsidie: 1.0,
        premium: 2.0,
        ccs_kosten: 0.80
    });
    const [displaySettings, setDisplaySettings] = useState({
        hydrogen_type: 'green',
        show_retail: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch waterstof prijzen
    const fetchHydrogenPrices = async (customSettings = null) => {
        setLoading(true);
        setError(null);
        
        try {
            let url = `${getBaseURL()}/hydrogen-price/`;
            let requestOptions = {
                method: 'GET',
                headers: getApiHeaders()
            };

            // Als we custom settings hebben, gebruik POST
            if (customSettings) {
                requestOptions.method = 'POST';
                requestOptions.body = JSON.stringify({
                    settings: customSettings
                });
            }

            const response = await fetch(url, requestOptions);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Converteer timestamps naar moment objecten
            data.data = data.data.map(item => ({
                ...item,
                tijd: moment(item.tijd),
                tijdLabel: moment(item.tijd).format('HH:mm')
            }));
            
            setPriceData(data);
            
        } catch (err) {
            console.error('Fout bij ophalen waterstof prijzen:', err);
            setError('Kon waterstof prijzen niet ophalen. Probeer het opnieuw.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch instellingen uit database
    const fetchSettings = async () => {
        try {
            const response = await fetch(`${getBaseURL()}/config`, {
                headers: getApiHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Converteer database parameters naar frontend format
            const settingsObject = {};
            const mapping = {
                'h2_forecast_hours': 'forecast_hours',
                'h2_a': 'a',
                'h2_b': 'b',
                'h2_compressie': 'compressie',
                'h2_opslag': 'opslag',
                'h2_transport': 'transport',
                'h2_infra': 'infra',
                'h2_marge': 'marge',
                'h2_subsidie': 'subsidie',
                'h2_premium': 'premium',
                'h2_ccs_kosten': 'ccs_kosten'
            };
            
            // Zet defaults eerst
            settingsObject.forecast_hours = 20;
            settingsObject.a = 0.05;
            settingsObject.b = 1.0;
            settingsObject.compressie = 1.5;
            settingsObject.opslag = 1.0;
            settingsObject.transport = 1.5;
            settingsObject.infra = 1.2;
            settingsObject.marge = 2.0;
            settingsObject.subsidie = 1.0;
            settingsObject.premium = 2.0;
            settingsObject.ccs_kosten = 0.80;
            
            // Override met database waarden
            if (data.configuration) {
                data.configuration.forEach(setting => {
                    if (mapping[setting.parameter]) {
                        const frontendKey = mapping[setting.parameter];
                        settingsObject[frontendKey] = parseFloat(setting.value);
                    }
                });
            }
            
            setSettings(settingsObject);
            
        } catch (err) {
            console.error('Fout bij ophalen instellingen:', err);
            // Zet defaults als er een fout optreedt
            setSettings({
                forecast_hours: 20,
                a: 0.05,
                b: 1.0,
                compressie: 1.5,
                opslag: 1.0,
                transport: 1.5,
                infra: 1.2,
                marge: 2.0,
                subsidie: 1.0,
                premium: 2.0,
                ccs_kosten: 0.80
            });
        }
    };

    // Fetch display instellingen uit database
    const fetchDisplaySettings = async () => {
        try {
            const response = await fetch(`${getBaseURL()}/hydrogen-price/display-settings`, {
                headers: getApiHeaders()
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setDisplaySettings(data);
            
            // Sync showRetail state met display settings
            setShowRetail(data.show_retail);
            
        } catch (err) {
            console.error('Fout bij ophalen display instellingen:', err);
            // Zet defaults als er een fout optreedt
            setDisplaySettings({
                hydrogen_type: 'green',
                show_retail: true
            });
        }
    };

    // Update instellingen via config endpoint (zoals SettingsPage)
    const updateSettings = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            // Mapping van frontend keys naar database parameter namen
            const mapping = {
                'forecast_hours': 'h2_forecast_hours',
                'a': 'h2_a',
                'b': 'h2_b',
                'compressie': 'h2_compressie',
                'opslag': 'h2_opslag',
                'transport': 'h2_transport',
                'infra': 'h2_infra',
                'marge': 'h2_marge',
                'subsidie': 'h2_subsidie',
                'premium': 'h2_premium',
                'ccs_kosten': 'h2_ccs_kosten'
            };
            
            // Sla elke parameter individueel op (net zoals SettingsPage)
            for (const [key, value] of Object.entries(settings)) {
                if (mapping[key]) {
                    const parameter = mapping[key];
                    
                    await fetch(`${getBaseURL()}/config`, {
                        method: 'POST',
                        headers: getApiHeaders(),
                        body: JSON.stringify({
                            parameter,
                            value: parseFloat(value)
                        })
                    });
                }
            }
            
            setSuccess('Instellingen succesvol bijgewerkt');
            
            // Sla waterstof prijzen op in database vanaf nu vooruit
            try {
                await post('/hydrogen-price/store-price-data', {
                    hours_forward: 48 // Sla komende 48 uur op
                });
                console.log('Waterstof prijzen opgeslagen in database vanaf nu vooruit');
            } catch (storeError) {
                console.warn('Kon waterstof prijzen niet opslaan in database:', storeError);
            }
            
            // Herbereken prijzen met nieuwe instellingen - forceer refresh na database update
            setTimeout(async () => {
                await fetchSettings(); // Haal nieuwe settings op uit database
                await fetchHydrogenPrices(); // Herbereken prijzen met database settings
            }, 500); // Kleine delay om database update te laten doorvoeren
            
        } catch (err) {
            console.error('Fout bij bijwerken instellingen:', err);
            setError('Kon instellingen niet bijwerken. Probeer het opnieuw.');
        } finally {
            setLoading(false);
        }
    };

    // Update display instellingen
    const updateDisplaySettings = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        
        try {
            const response = await fetch(`${getBaseURL()}/hydrogen-price/display-settings`, {
                method: 'PUT',
                headers: getApiHeaders(),
                body: JSON.stringify(displaySettings)
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            setDisplaySettings(data);
            setShowRetail(data.show_retail);
            
            setSuccess('Display instellingen succesvol bijgewerkt');
            
        } catch (err) {
            console.error('Fout bij bijwerken display instellingen:', err);
            setError('Kon display instellingen niet bijwerken');
        } finally {
            setLoading(false);
        }
    };

    // Export naar CSV functie
    const exportToCSV = () => {
        if (!priceData || !priceData.data) {
            setError('Geen data beschikbaar voor export');
            return;
        }

        try {
            // Bereken retail opslag voor consistent gebruik
            const retailOpslag = settings.compressie + settings.opslag + settings.transport + settings.infra + settings.marge;
            
            // CSV Header
            const headers = [
                'Tijd',
                'Timestamp (UTC)',
                'TTF Prijs (€/MWh)',
                'Grijze H₂ Markt (€/kg)',
                'Groene H₂ Markt (€/kg)', 
                'Blauwe H₂ Markt (€/kg)',
                'Grijze H₂ Retail (€/kg)',
                'Groene H₂ Retail (€/kg)',
                'Blauwe H₂ Retail (€/kg)',
                'Retail Opslag (€/kg)',
                'Compressie (€/kg)',
                'Opslag (€/kg)',
                'Transport (€/kg)',
                'Infrastructuur (€/kg)',
                'Marge (€/kg)'
            ];

            // Data rijen
            const csvData = priceData.data.map(item => [
                item.tijdLabel,
                item.tijd.toISOString(),
                priceData.ttf_price_current?.toFixed(2) || '0.00',
                item.grijze_h2?.toFixed(2) || '0.00',
                item.groene_h2?.toFixed(2) || '0.00',
                item.blauwe_h2?.toFixed(2) || '0.00',
                (item.grijze_h2 + retailOpslag).toFixed(2),
                (item.groene_h2 + retailOpslag).toFixed(2),
                (item.blauwe_h2 + retailOpslag).toFixed(2),
                retailOpslag.toFixed(2),
                settings.compressie?.toFixed(2) || '0.00',
                settings.opslag?.toFixed(2) || '0.00',
                settings.transport?.toFixed(2) || '0.00',
                settings.infra?.toFixed(2) || '0.00',
                settings.marge?.toFixed(2) || '0.00'
            ]);

            // Combineer headers en data
            const csvContent = [headers, ...csvData]
                .map(row => row.map(field => `"${field}"`).join(','))
                .join('\n');

            // Maak downloadbaar bestand
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            
            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                
                // Bestandsnaam met huidige datum en tijd
                const now = moment().format('YYYY-MM-DD_HH-mm');
                link.setAttribute('download', `waterstof_prijzen_${now}.csv`);
                
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setSuccess('CSV bestand succesvol gedownload');
            }
        } catch (err) {
            console.error('Fout bij CSV export:', err);
            setError('Kon CSV niet exporteren. Probeer het opnieuw.');
        }
    };

    // Initiële data ophalen
    useEffect(() => {
        fetchSettings();
        fetchDisplaySettings();
        fetchHydrogenPrices();
    }, []);

    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Settings input handler
    const handleSettingChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: parseFloat(value) || 0
        }));
    };

    // Custom Legend component
    const CustomLegend = (props) => {
        const legendItems = [
            { key: 'Grijze H₂ Lijn', name: 'Grijze H₂', color: '#666666' },
            { key: 'Groene H₂ Lijn', name: 'Groene H₂', color: '#4CAF50' },
            { key: 'Blauwe H₂ Lijn', name: 'Blauwe H₂', color: '#2196F3' }
        ];

        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: 3, 
                pb: 2, 
                pt: 1 
            }}>
                {legendItems.map(item => {
                    const isHidden = hiddenLines[item.key];
                    return (
                        <Box 
                            key={item.key}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                cursor: 'pointer',
                                opacity: isHidden ? 0.4 : 1,
                                '&:hover': { opacity: isHidden ? 0.6 : 0.8 }
                            }}
                            onClick={() => handleLegendClick({ dataKey: item.key })}
                        >
                            <Box 
                                sx={{ 
                                    width: 16, 
                                    height: 3, 
                                    backgroundColor: isHidden ? '#999999' : item.color,
                                    mr: 1,
                                    borderRadius: 1
                                }} 
                            />
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    color: isHidden ? '#999999' : 'text.primary',
                                    fontWeight: isHidden ? 'normal' : 'bold'
                                }}
                            >
                                {item.name}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // Legend click handler voor het verbergen/tonen van lijnen
    const handleLegendClick = (data) => {
        // Alleen reageren op lijn items (niet de bar componenten)
        const lineKeys = ['Grijze H₂ Lijn', 'Groene H₂ Lijn', 'Blauwe H₂ Lijn'];
        if (lineKeys.includes(data.dataKey)) {
            setHiddenLines(prev => ({
                ...prev,
                [data.dataKey]: !prev[data.dataKey]
            }));
        }
    };

    // Bereid data voor voor gecombineerde grafiek - met useMemo voor betere performance en correcte updates
    const chartData = useMemo(() => {
        if (!priceData) return [];
        
        return priceData.data.map((item, index) => {
            // Bereken retail opslag voor deze berekening
            const retailOpslag = settings.compressie + settings.opslag + settings.transport + settings.infra + settings.marge;
            
            const baseData = {
                tijd: item.tijdLabel,
                ...(showRetail ? {
                    // Bereken retail prijzen dynamisch op basis van frontend settings
                    'Grijze H₂ Lijn': item.grijze_h2 + retailOpslag,
                    'Groene H₂ Lijn': item.groene_h2 + retailOpslag,
                    'Blauwe H₂ Lijn': item.blauwe_h2 + retailOpslag
                } : {
                    // Gebruik marktprijzen direct uit backend data
                    'Grijze H₂ Lijn': item.grijze_h2,
                    'Groene H₂ Lijn': item.groene_h2,
                    'Blauwe H₂ Lijn': item.blauwe_h2
                })
            };

            // Voeg prijsopbouw data toe als retail en breakdown aan staan
            // Voor alle forecast uren (of maximum 24 voor leesbaarheid)
            const maxBars = Math.min(settings.forecast_hours, 24);
            if (showRetail && showBreakdown && index < maxBars) {
                baseData['Grijze H₂ Basis'] = item.grijze_h2;
                // Gebruik actuele frontend settings in plaats van backend data
                baseData['Compressie'] = settings.compressie;
                baseData['Opslag'] = settings.opslag;
                baseData['Transport'] = settings.transport;
                baseData['Infrastructuur'] = settings.infra;
                baseData['Marge'] = settings.marge;
            }

            return baseData;
        });
    }, [priceData, showRetail, showBreakdown, settings.forecast_hours, settings.compressie, settings.opslag, settings.transport, settings.infra, settings.marge]);

    // Bereken KPI waarden - reactief op data en settings changes
    const kpis = useMemo(() => {
        if (!priceData) return null;

        // Bereken netto marktprijs (marktprijs zonder opslag componenten)
        const greenH2MarketPrices = priceData.data.map(item => item.groene_h2);
        const nettoMarketPriceGreen = greenH2MarketPrices.reduce((sum, price) => sum + price, 0) / greenH2MarketPrices.length;

        // Bereken retail opslag (alle retail componenten bij elkaar)
        const retailOpslag = settings.compressie + settings.opslag + settings.transport + 
                           settings.infra + settings.marge;

        // Bereken correcte retail prijs door marktprijs + retail opslag
        const avgRetailPriceGreen = nettoMarketPriceGreen + retailOpslag;
        const minRetailPriceGreen = Math.min(...greenH2MarketPrices) + retailOpslag;
        const maxRetailPriceGreen = Math.max(...greenH2MarketPrices) + retailOpslag;

        // Bereken TTF prijs per kWh (1 MWh = 1000 kWh)
        const ttfPricePerKwh = priceData.ttf_price_current / 1000;

        return {
            ttfPrice: priceData.ttf_price_current,
            ttfPricePerKwh,
            avgRetailPriceGreen,
            minRetailPriceGreen,
            maxRetailPriceGreen,
            nettoMarketPriceGreen,
            retailOpslag
        };
    }, [priceData, settings.compressie, settings.opslag, settings.transport, settings.infra, settings.marge]);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Waterstof Prijzen
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab 
                        icon={<ShowChartIcon />} 
                        label="Prijzengrafiek" 
                        value={0}
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<SettingsIcon />} 
                        label="Instellingen" 
                        value={1}
                        iconPosition="start"
                    />
                </Tabs>
                <Button 
                    variant="outlined" 
                    startIcon={<DownloadIcon />}
                    onClick={exportToCSV}
                    disabled={loading || !priceData}
                >
                    Exporteer CSV
                </Button>
            </Box>

            {/* KPI Cards */}
            {priceData && kpis && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h6" color="textSecondary">
                                        Huidige TTF prijs
                                    </Typography>
                                    <Typography variant="h4">
                                        €{kpis.ttfPrice.toFixed(2)}/MWh
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        €{kpis.ttfPricePerKwh.toFixed(4)} prijs per kWh
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h6" color="textSecondary">
                                        Retail prijs Groene H₂
                                    </Typography>
                                    <Typography variant="h4">
                                        €{kpis.avgRetailPriceGreen.toFixed(2)}/kg
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        €{kpis.minRetailPriceGreen.toFixed(2)} – €{kpis.maxRetailPriceGreen.toFixed(2)}/kg
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h6" color="textSecondary">
                                        Netto marktprijs Groene H₂
                                    </Typography>
                                    <Typography variant="h4">
                                        €{kpis.nettoMarketPriceGreen.toFixed(2)}/kg
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        (zonder opslag)
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="h6" color="textSecondary">
                                        Retail-opslag
                                    </Typography>
                                    <Typography variant="h4">
                                        €{kpis.retailOpslag.toFixed(2)}/kg
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        (marge + ketenkosten)
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardContent>

                    {/* Tab 1: Prijzengrafiek */}
                    {tabValue === 0 && (
                        <Box>
                            {/* Controls */}
                            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showRetail}
                                            onChange={(e) => setShowRetail(e.target.checked)}
                                        />
                                    }
                                    label="Retail prijzen tonen"
                                />
                                
                                {showRetail && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={showBreakdown}
                                                onChange={(e) => setShowBreakdown(e.target.checked)}
                                            />
                                        }
                                        label="Prijsopbouw tonen"
                                    />
                                )}
                                
                                <Button 
                                    variant="outlined" 
                                    onClick={() => fetchHydrogenPrices()}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={20} /> : 'Vernieuwen'}
                                </Button>
                            </Box>

                            {/* Gecombineerde grafiek */}
                            {priceData && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        {showRetail ? 'Retail Prijzen' : 'Markt Prijzen'} (€/kg)
                                        {showRetail && showBreakdown && (
                                            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                                                - Prijsopbouw getoond voor eerste {Math.min(settings.forecast_hours, 24)} uur
                                            </Typography>
                                        )}
                                    </Typography>
                                    
                                    {/* Custom Legend boven de grafiek */}
                                    <CustomLegend />
                                    
                                    <ResponsiveContainer width="100%" height={450}>
                                        <ComposedChart 
                                            data={chartData}
                                            key={`chart-${JSON.stringify({
                                                dataLength: chartData.length,
                                                showRetail,
                                                showBreakdown,
                                                settingsHash: Object.values(settings).join('-')
                                            })}`}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis 
                                                dataKey="tijd" 
                                                tick={{ fontSize: 12 }}
                                                interval="preserveStartEnd"
                                            />
                                            <YAxis tick={{ fontSize: 12 }} domain={[0, 'dataMax']} />
                                            <Tooltip 
                                                formatter={(value, name) => [
                                                    `€${value.toFixed(2)}/kg`, 
                                                    name
                                                ]}
                                                labelFormatter={(label) => `Tijd: ${label}`}
                                            />
                                            
                                            {/* Stacked bars voor prijsopbouw (alleen als breakdown aan staat) */}
                                            {showRetail && showBreakdown && (
                                                <>
                                                    <Bar dataKey="Grijze H₂ Basis" stackId="breakdown" fill="#999999" name="Grijze H₂ Basis" />
                                                    <Bar dataKey="Compressie" stackId="breakdown" fill="#FF9800" name="Compressie" />
                                                    <Bar dataKey="Opslag" stackId="breakdown" fill="#F44336" name="Opslag" />
                                                    <Bar dataKey="Transport" stackId="breakdown" fill="#9C27B0" name="Transport" />
                                                    <Bar dataKey="Infrastructuur" stackId="breakdown" fill="#607D8B" name="Infrastructuur" />
                                                    <Bar dataKey="Marge" stackId="breakdown" fill="#795548" name="Marge" />
                                                </>
                                            )}
                                            
                                            {/* Lijnen voor waterstof prijzen */}
                                            {!hiddenLines['Grijze H₂ Lijn'] && (
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="Grijze H₂ Lijn" 
                                                    stroke="#666666" 
                                                    strokeWidth={3}
                                                    dot={{ r: 4 }}
                                                    name="Grijze H₂"
                                                    connectNulls={false}
                                                />
                                            )}
                                            {!hiddenLines['Groene H₂ Lijn'] && (
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="Groene H₂ Lijn" 
                                                    stroke="#4CAF50" 
                                                    strokeWidth={3}
                                                    dot={{ r: 4 }}
                                                    name="Groene H₂"
                                                    connectNulls={false}
                                                />
                                            )}
                                            {!hiddenLines['Blauwe H₂ Lijn'] && (
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="Blauwe H₂ Lijn" 
                                                    stroke="#2196F3" 
                                                    strokeWidth={3}
                                                    dot={{ r: 4 }}
                                                    name="Blauwe H₂"
                                                    connectNulls={false}
                                                />
                                            )}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Tab 2: Instellingen */}
                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Prijsberekening Instellingen
                            </Typography>
                            
                            <Grid container spacing={3}>
                                {/* Algemene instellingen */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">Algemene Instellingen</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Forecast Uren"
                                                        type="number"
                                                        value={settings.forecast_hours}
                                                        onChange={(e) => handleSettingChange('forecast_hours', e.target.value)}
                                                        helperText="Aantal uren vooruit te voorspellen"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="TTF Coëfficiënt (a)"
                                                        type="number"
                                                        step="0.01"
                                                        value={settings.a}
                                                        onChange={(e) => handleSettingChange('a', e.target.value)}
                                                        helperText="TTF prijs multiplicator"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Basisprijs (b)"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.b}
                                                        onChange={(e) => handleSettingChange('b', e.target.value)}
                                                        helperText="Vaste basisprijs (€/kg)"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Display instellingen */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">Grafiek Display Instellingen</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        label="Standaard Waterstof Type"
                                                        value={displaySettings.hydrogen_type}
                                                        onChange={(e) => setDisplaySettings(prev => ({
                                                            ...prev,
                                                            hydrogen_type: e.target.value
                                                        }))}
                                                        helperText="Welk waterstof type standaard getoond wordt in planning"
                                                    >
                                                        <MenuItem value="green">Groen</MenuItem>
                                                        <MenuItem value="grey">Grijs</MenuItem>
                                                        <MenuItem value="blue">Blauw</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={displaySettings.show_retail}
                                                                onChange={(e) => setDisplaySettings(prev => ({
                                                                    ...prev,
                                                                    show_retail: e.target.checked
                                                                }))}
                                                            />
                                                        }
                                                        label="Standaard Retail Prijzen Tonen"
                                                        sx={{ mt: 2 }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                                                        Of wholesale (markt) prijzen getoond worden in planning
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                                <Button 
                                                    variant="contained" 
                                                    color="secondary"
                                                    onClick={updateDisplaySettings}
                                                    disabled={loading}
                                                >
                                                    {loading ? <CircularProgress size={20} /> : 'Display Instellingen Opslaan'}
                                                </Button>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Retailcomponenten */}
                                <Grid item xs={12}>
                                    <Accordion defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">Retailcomponenten (€/kg)</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Compressie"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.compressie}
                                                        onChange={(e) => handleSettingChange('compressie', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Opslag"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.opslag}
                                                        onChange={(e) => handleSettingChange('opslag', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Transport"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.transport}
                                                        onChange={(e) => handleSettingChange('transport', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Infrastructuur"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.infra}
                                                        onChange={(e) => handleSettingChange('infra', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Marge"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.marge}
                                                        onChange={(e) => handleSettingChange('marge', e.target.value)}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Waterstof type specifieke instellingen */}
                                <Grid item xs={12}>
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">Type-specifieke Instellingen (€/kg)</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Subsidie (Groen)"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.subsidie}
                                                        onChange={(e) => handleSettingChange('subsidie', e.target.value)}
                                                        helperText="Subsidie voor groene waterstof"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Premium (Groen)"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.premium}
                                                        onChange={(e) => handleSettingChange('premium', e.target.value)}
                                                        helperText="Premium voor groene waterstof"
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="CCS Kosten (Blauw)"
                                                        type="number"
                                                        step="0.1"
                                                        value={settings.ccs_kosten}
                                                        onChange={(e) => handleSettingChange('ccs_kosten', e.target.value)}
                                                        helperText="CO₂ opslag kosten"
                                                    />
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Grid>

                                {/* Actie knoppen */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                        <Button 
                                            variant="contained" 
                                            onClick={updateSettings}
                                            disabled={loading}
                                        >
                                            {loading ? <CircularProgress size={20} /> : 'Opslaan en Toepassen'}
                                        </Button>
                                        <Button 
                                            variant="outlined" 
                                            onClick={fetchSettings}
                                        >
                                            Reset
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Error Snackbar */}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>

            {/* Success Snackbar */}
            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HydrogenPricePage; 